/**
 * Typed Radar product-analytics adapter — the ONLY way Radar surfaces emit
 * product events. Owns the approved event names and property types, buckets
 * high-cardinality values (durations, scores) at the boundary, and no-ops
 * safely when PostHog is unavailable or consent is absent.
 *
 * Destination policy: Radar product events are PostHog-only. They are
 * high-volume (per-tool completions/errors) and must NOT be sent to GA4 —
 * GA4 stays the acquisition/marketing baseline. The generic trackEvent()
 * helper handles the events that dual-fire to both.
 *
 * Never pass emails, audited domains/URLs, advisor text, tokens, raw API
 * or exception messages, report contents, or any free-form user input.
 * The property types below make that unrepresentable; posthog-client's
 * before_send denylist is the final backstop.
 */

import { capturePostHogEvent } from './posthog-client'
import type { ToolKey } from '@/types/radar'
import type { PaddleProduct } from '@/lib/paddle/checkout'
import type { FreeToolSlug } from '@/lib/tool-access'

type UserTier = 'free' | 'paid'
export type CheckoutEntryPoint =
  | 'gate'
  | 'upgrade_modal'
  | 'pack_modal'
  | 'pricing_page'
  | 'account_dashboard'
  | 'tool_teaser'
  | 'platform_teaser'
  | 'watch_domain'
type AdvisorEntryPoint = 'fab' | 'entry_card'

/**
 * Arabic pilot dimensions. Re-declared from the page's own closed unions
 * rather than imported, so a future edit to the page cannot silently widen
 * what this adapter is willing to send. A compile error there is the point.
 */
type ArabicMarket = 'saudi' | 'uae' | 'gcc'
type ArabicLanguage = 'ar' | 'en' | 'bilingual'
/**
 * Fixed failure categories — never a server message or raw error text.
 *
 * `not_persisted` is its own outcome: /api/contact answers 201 even when the
 * contact_requests insert failed (the notification email still goes out), so
 * without it a database failure would be counted as a completed conversion.
 */
type ArabicPilotFailure = 'server_rejected' | 'network_error' | 'not_persisted'
type ExportFormat = 'pdf' | 'json'
type SaveOutcome = 'database' | 'local_only'
type ShareOutcome = 'share_url' | 'fallback'
type ToolFailureOutcome = 'http_error' | 'network_error'

type Grade = 'A' | 'B' | 'C' | 'D' | 'F' | 'unknown' | 'withheld'
type ScoreBucket = '0-19' | '20-39' | '40-59' | '60-79' | '80-100' | 'withheld'
type ScoreStatus = 'graded' | 'withheld'
type DurationBucket =
  | 'lt_10s'
  | '10s_30s'
  | '30s_60s'
  | '1m_2m'
  | '2m_5m'
  | 'gte_5m'

const GRADES = new Set(['A', 'B', 'C', 'D', 'F'])

function toGrade(grade: string): Grade {
  return GRADES.has(grade) ? (grade as Grade) : 'unknown'
}

function scoreBucket(score: number): ScoreBucket {
  if (score < 20) return '0-19'
  if (score < 40) return '20-39'
  if (score < 60) return '40-59'
  if (score < 80) return '60-79'
  return '80-100'
}

function durationBucket(ms: number): DurationBucket {
  if (ms < 10_000) return 'lt_10s'
  if (ms < 30_000) return '10s_30s'
  if (ms < 60_000) return '30s_60s'
  if (ms < 120_000) return '1m_2m'
  if (ms < 300_000) return '2m_5m'
  return 'gte_5m'
}

type Destination = 'posthog'

/**
 * Single emit point. Radar events support explicit destinations; only
 * 'posthog' is permitted — adding 'ga4' here is a policy change, not a
 * code change, and needs sign-off first.
 */
function emit(
  name: string,
  props?: Record<string, string | number | boolean>,
  destinations: Destination[] = ['posthog']
): void {
  try {
    if (destinations.includes('posthog')) {
      capturePostHogEvent(name, props)
    }
  } catch {
    // Analytics must never break the product.
  }
}

export const radarAnalytics = {
  /** The $5 paywall gate rendered for a non-paid visitor. */
  gateViewed(): void {
    emit('radar_gate_viewed')
  },

  checkoutStarted(product: PaddleProduct, entryPoint: CheckoutEntryPoint) {
    emit('radar_checkout_started', { product, entry_point: entryPoint })
  },

  /** From Paddle's eventCallback — no payload fields are read (PII). */
  checkoutCompleted(): void {
    emit('radar_checkout_completed')
  },

  checkoutFailed(): void {
    emit('radar_checkout_failed')
  },

  /** Fired once per audit run, after the charge succeeded. */
  auditStarted(userTier: UserTier): void {
    emit('radar_audit_started', { user_tier: userTier })
  },

  toolCompleted(toolId: ToolKey, grade: string, durationMs: number): void {
    emit('radar_tool_completed', {
      tool_id: toolId,
      grade: toGrade(grade),
      duration_bucket: durationBucket(durationMs),
    })
  },

  toolFailed(
    toolId: ToolKey,
    httpStatus: number | null,
    durationMs: number
  ): void {
    const outcome: ToolFailureOutcome =
      httpStatus === null ? 'network_error' : 'http_error'
    emit('radar_tool_failed', {
      tool_id: toolId,
      outcome,
      ...(httpStatus === null ? {} : { http_status: httpStatus }),
      duration_bucket: durationBucket(durationMs),
    })
  },

  auditCompleted(input: {
    userTier: UserTier
    score: number | null
    grade: string
    successfulToolCount: number
    failedToolCount: number
    durationMs: number
  }): void {
    const scoreStatus: ScoreStatus =
      input.score === null ? 'withheld' : 'graded'
    emit('radar_audit_completed', {
      user_tier: input.userTier,
      score_status: scoreStatus,
      grade: input.score === null ? 'withheld' : toGrade(input.grade),
      score_bucket:
        input.score === null ? 'withheld' : scoreBucket(input.score),
      successful_tool_count: input.successfulToolCount,
      failed_tool_count: input.failedToolCount,
      duration_bucket: durationBucket(input.durationMs),
    })
  },

  reportExported(format: ExportFormat): void {
    emit('radar_report_exported', { export_format: format })
  },

  reportSaved(outcome: SaveOutcome): void {
    emit('radar_report_saved', { outcome })
  },

  reportShared(outcome: ShareOutcome): void {
    emit('radar_report_shared', { outcome })
  },

  /** First strategist interaction of the session (outcome, not click). */
  advisorOpened(entryPoint: AdvisorEntryPoint): void {
    emit('radar_advisor_opened', { entry_point: entryPoint })
  },

  advisorLimitReached(): void {
    emit('radar_advisor_limit_reached')
  },

  /**
   * The shared /tools/* free-run access gate (email + OTP), used by every
   * standalone free tool via useToolAccessGate. Distinct from gateViewed()
   * above, which is the paid $5 audit gate on /platform/app.
   */
  toolAccessGateViewed(toolSlug: FreeToolSlug): void {
    emit('tool_access_gate_viewed', { tool_slug: toolSlug })
  },

  toolAccessGateCodeRequested(toolSlug: FreeToolSlug): void {
    emit('tool_access_gate_code_requested', { tool_slug: toolSlug })
  },

  /**
   * The gate granted access. `method` is `otp` after verify-code succeeds,
   * `bypass` when the request-code response short-circuits verification
   * (not OTP — must stay distinguishable from a real code verification).
   */
  toolAccessGateGranted(
    toolSlug: FreeToolSlug,
    method: 'otp' | 'bypass'
  ): void {
    emit('tool_access_gate_granted', { tool_slug: toolSlug, method })
  },

  toolAccessGateRunConsumed(toolSlug: FreeToolSlug): void {
    emit('tool_access_gate_run_consumed', { tool_slug: toolSlug })
  },

  /**
   * The /ar/radar Arabic pilot request modal.
   *
   * Why these exist: the 2026-09-19 audit found the radar_sa_ar_pilot campaign
   * had 111 attributed visitors and zero measurable conversions. The cause was
   * not the offer — the page emitted NO events at all beyond pageviews, and
   * its modal posts to the generic /api/contact endpoint, so the generic
   * contact funnel could never describe it. The campaign was unmeasurable, not
   * proven to have failed.
   *
   * Properties are closed unions only (`MarketKey`, `LanguageKey`, and the
   * fixed failure reasons below). The campaign name is deliberately NOT sent:
   * a raw campaign string is attacker- and operator-supplied free text that
   * could carry an address, and the before_send denylist filters property
   * NAMES, not values. Campaign stays on the server-side lead record where it
   * is allowlisted and bounded.
   */
  arabicPilotModalOpened(market: ArabicMarket, language: ArabicLanguage): void {
    emit('arabic_pilot_modal_opened', { market, language })
  },

  arabicPilotFormStarted(market: ArabicMarket, language: ArabicLanguage): void {
    emit('arabic_pilot_form_started', { market, language })
  },

  arabicPilotSubmitted(market: ArabicMarket, language: ArabicLanguage): void {
    emit('arabic_pilot_submitted', { market, language })
  },

  /**
   * A submission that did not create a request. `reason` is a fixed category,
   * never the server's response body — that is untrusted text and has no place
   * in an analytics payload.
   */
  arabicPilotRejected(
    market: ArabicMarket,
    language: ArabicLanguage,
    reason: ArabicPilotFailure
  ): void {
    emit('arabic_pilot_rejected', { market, language, reason })
  },
}
